// 9fbef606107a605d69c0edbcd8029e5d
import React, { useRef, useState, useMemo } from 'react';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Carousel from '@tcp/core/src/components/common/molecules/Carousel/container/index.async';
import useInViewWithDelay from '@tcp/web/src/hooks/useInViewWithDelay';
import CarouselContainer from '@tcp/core/src/components/common/molecules/Carousel/container/Carousel.container';
import { getIconPath, getViewportInfo, isClient } from '@tcp/core/src/utils';
import { capitalizeWords } from '@tcp/core/src/utils/utils';
import RecommendationTile from '@tcp/core/src/components/common/molecules/RecommendationCarousel/views/RecommendationTile.view';
import config from '../config';
import style from '../styles/RecommendationCarousel.style';

const getHeaderLabel = ({ variation, moduleOHeaderLabel, modulePHeaderLabel }) => {
  return variation === config.variations.moduleO ? moduleOHeaderLabel : modulePHeaderLabel;
};

const showHeader = ({
  isFavoriteRecommendation,
  isSuggestedItem,
  accordionHeader,
  portalValue,
}) => {
  return (
    !isFavoriteRecommendation &&
    !isSuggestedItem &&
    !accordionHeader &&
    portalValue !== Constants.RECOMMENDATIONS_MBOXNAMES.IN_GRID_PLP_PRODUCTS
  );
};

const renderHeader = ({ headerLabel, params, fromOrderDetails }) => {
  return (
    <BodyCopy
      className={!fromOrderDetails ? 'recommendations-header-new' : ''}
      fontSize="fs16"
      component="div"
      fontFamily="secondary"
      fontWeight="bold"
      dataLocator={params?.dataLocator || ''}
      id="recommendation"
    >
      {headerLabel}
    </BodyCopy>
  );
};

const renderSubHeader = ({ subHeaderLabel }) => {
  return (
    <BodyCopy
      fontSize="fs14"
      component="div"
      fontFamily="secondary"
      fontWeight="regular"
      className="elem-mt-XXS elem-mb-MED "
      id="recommendation"
    >
      {subHeaderLabel}
    </BodyCopy>
  );
};

const renderAccordionHeader = ({ headerLabel, handleAccordionToggle, accordionOpenClass }) => {
  return (
    <BodyCopy
      className={`accordion-header-new ${accordionOpenClass}`}
      fontSize="fs16"
      component="div"
      fontFamily="secondary"
      fontWeight="bold"
      onClick={handleAccordionToggle}
      id="recommendation"
    >
      {headerLabel}
    </BodyCopy>
  );
};

const getMinProductsLength = (isAddedToBag, isMobile) => {
  let minProductsLength = 3;
  if (!isMobile) {
    minProductsLength = isAddedToBag
      ? config.CAROUSEL_OPTIONS_ADDED_TO_BAG.slidesToShow
      : config.CAROUSEL_OPTIONS.slidesToShow;
  }
  return minProductsLength;
};

const handleScroll = (
  event,
  products,
  visibleCardIndexes,
  setVisibleCardIndexes,
  firedCardIndexes,
  setAndFireProducts,
  isMobile
) => {
  const container = event.target;
  const cardWidth = container.querySelector('.recommendation-tile').offsetWidth; // Get card width
  const { scrollLeft } = container || {};
  const PRODUCTS_IN_VIEW = isMobile ? 3 : 5;
  const visibleCards = new Set();
  const numVisibleCards = Math.ceil(container.offsetWidth / cardWidth);
  for (let i = 0; i < numVisibleCards; i += 1) {
    const cardIndex = Math.floor((scrollLeft + i * cardWidth) / cardWidth);
    if (
      cardIndex >= PRODUCTS_IN_VIEW &&
      cardIndex < products.length &&
      !visibleCardIndexes.has(cardIndex) &&
      !firedCardIndexes.has(cardIndex)
    ) {
      visibleCardIndexes.add(cardIndex);
      visibleCards.add(cardIndex);
    }
  }

  if (visibleCards.size) {
    setAndFireProducts();
  }
};

const renderRecommendations = ({
  products,
  isHideOriginalPriceAndMarchantTagEnabled,
  isDynamicBadgeEnabled,
  headerLabel,
  page,
  currencySymbol,
  currencyAttributes,
  onQuickViewOpenClick,
  bagItemCount,
  isBagPage,
  isAddedToBag,
  isBackToQuickviewCTAEnabled,
  isQvModalOpen,
  closeQvModal,
  promotionsData,
  serverTime,
  isPromoMsgEnabledRecommendation,
  loadSendEventsToMonetate,
  isDynamicBadgeEnabledAbtest,
  isTransparentImageSizeReduceEnabled,
}) => {
  const nonEmptyBag = bagItemCount > 0;
  return products?.map((product, index) => {
    const productId = product.generalProductId;
    return (
      <RecommendationTile
        key={productId}
        product={product}
        index={index}
        isHideOriginalPriceAndMarchantTagEnabled={isHideOriginalPriceAndMarchantTagEnabled}
        isDynamicBadgeEnabled={isDynamicBadgeEnabled}
        headerLabel={headerLabel}
        page={page}
        currencySymbol={currencySymbol}
        currencyAttributes={currencyAttributes}
        onQuickViewOpenClick={onQuickViewOpenClick}
        bagItemCount={nonEmptyBag}
        isBagPage={isBagPage}
        isAddedToBag={isAddedToBag}
        isBackToQuickviewCTAEnabled={isBackToQuickviewCTAEnabled}
        isQvModalOpen={isQvModalOpen}
        closeQvModal={closeQvModal}
        promotionsData={promotionsData}
        serverTime={serverTime}
        isPromoMsgEnabledRecommendation={isPromoMsgEnabledRecommendation}
        sendEventsToMonetateActn={loadSendEventsToMonetate}
        isDynamicBadgeEnabledAbtest={isDynamicBadgeEnabledAbtest}
        isTransparentImageSizeReduceEnabled={isTransparentImageSizeReduceEnabled}
      />
    );
  });
};

const renderRecommendationList = ({
  products,
  variation,
  isHideOriginalPriceAndMarchantTagEnabled,
  isDynamicBadgeEnabled,
  headerLabel,
  page,
  currencySymbol,
  currencyAttributes,
  onQuickViewOpenClick,
  showSkeletonWithLazyLoad,
  isAddedToBag,
  bagItemCount,
  isBagPage,
  isBackToQuickviewCTAEnabled,
  isQvModalOpen,
  closeQvModal,
  serverTime,
  promotionsData,
  isPromoMsgEnabledRecommendation,
  visibleCardIndexes,
  setVisibleCardIndexes,
  loadSendEventsToMonetate,
  recTokens,
  firedCardIndexes,
  setAndFireProducts,
  isMobile,
  isDynamicBadgeEnabledAbtest,
  isTransparentImageSizeReduceEnabled,
}) => {
  const minProductsLength = isAddedToBag
    ? config.CAROUSEL_OPTIONS_ADDED_TO_BAG.slidesToShow
    : config.CAROUSEL_OPTIONS.slidesToShow;

  const isMobileOrTablet = isClient()
    ? getViewportInfo().isMobile || getViewportInfo().isTablet
    : null;

  const props = {
    variation,
    products,
    isHideOriginalPriceAndMarchantTagEnabled,
    isDynamicBadgeEnabled,
    headerLabel,
    page,
    currencySymbol,
    currencyAttributes,
    onQuickViewOpenClick,
    bagItemCount,
    isBagPage,
    isAddedToBag,
    isBackToQuickviewCTAEnabled,
    isQvModalOpen,
    closeQvModal,
    serverTime,
    promotionsData,
    isPromoMsgEnabledRecommendation,
    loadSendEventsToMonetate,
    isDynamicBadgeEnabledAbtest,
    isTransparentImageSizeReduceEnabled,
  };
  const CarouselView = showSkeletonWithLazyLoad ? CarouselContainer : Carousel;
  if (isMobileOrTablet) {
    return (
      <div
        onScroll={(event) =>
          handleScroll(
            event,
            products,
            visibleCardIndexes,
            setVisibleCardIndexes,
            firedCardIndexes,
            setAndFireProducts,
            isMobile
          )
        }
        className="scroll-container-mobile-tablet"
      >
        {renderRecommendations(props)}
      </div>
    );
  }
  if (products?.length > minProductsLength) {
    return (
      <CarouselView
        className="recommendation-carousel-new"
        loadSendEventsToMonetate={loadSendEventsToMonetate}
        recTokens={recTokens}
        options={isAddedToBag ? config.CAROUSEL_OPTIONS_ADDED_TO_BAG : config.CAROUSEL_OPTIONS}
        carouselConfig={{
          customArrowLeft: getIconPath('icon-carrot-grey-large'),
          customArrowRight: getIconPath('icon-carrot-grey-large'),
          dataLocatorCarousel: `${variation}-variation`,
        }}
      >
        {renderRecommendations(props)}
      </CarouselView>
    );
  }

  return <div className="no-carousel-container-desktop">{renderRecommendations(props)}</div>;
};

const RecommendationCarousel = (props) => {
  const eventsFiredRef = useRef(null);
  const timeoutRef = useRef(null);
  const {
    className,
    isPdpPlaProducts,
    isHideOriginalPriceAndMarchantTagEnabled,
    products,
    isFavoriteRecommendation,
    isSuggestedItem,
    accordionHeader,
    portalValue,
    page,
    moduleOHeaderLabel,
    modulePHeaderLabel,
    variations,
    isDynamicBadgeEnabled,
    currencySymbol,
    currencyAttributes,
    onQuickViewOpenClick,
    isPDP,
    isBagPage,
    isHomePage,
    isAddedToBag,
    isBIACombinedCarouselEnabled,
    fromBIA,
    bagItemCount,
    isBackToQuickviewCTAEnabled,
    isQvModalOpen,
    closeQvModal,
    fromOrderDetails,
    subHeaderLabel,
    serverTime,
    promotionsData,
    isPromoMsgEnabledRecommendation,
    loadSendEventsToMonetate,
    isDynamicBadgeEnabledAbtest,
    isTransparentImageSizeReduceEnabled,
  } = props;

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [visibleCardIndexes, setVisibleCardIndexes] = useState(new Set());
  const [firedCardIndexes, setFiredCardIndexes] = useState(new Set());

  const showSkeletonWithLazyLoad = isPDP || isBagPage || isHomePage;
  const handleAccordionToggle = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  const accordionOpenClass = !isAccordionOpen ? 'hide-recommendations' : '';

  const headerLabel = getHeaderLabel({ variations, moduleOHeaderLabel, modulePHeaderLabel });
  const headerLabelCapitalized = capitalizeWords(headerLabel || '');
  const params = config.params[variations];
  const shouldShowHeader = showHeader({
    isFavoriteRecommendation,
    isSuggestedItem,
    accordionHeader,
    portalValue,
  });

  const recTokens = useMemo(
    () => (Array.isArray(products) ? products.map((product) => product?.recToken) : []),
    [products]
  );
  const { isMobile } = getViewportInfo() || {};

  const [ref, isVisible] = useInViewWithDelay({ threshold: 0.5 }, 1000);
  if (isVisible && !eventsFiredRef.current) {
    eventsFiredRef.current = true;
    const minProductsLength = getMinProductsLength(isAddedToBag, isMobile);
    const numObjectsToProcess = Math.min(products.length, minProductsLength);
    const itemsInView = recTokens.slice(0, numObjectsToProcess);
    if (itemsInView?.length > 0) loadSendEventsToMonetate({ recToken: itemsInView });
  }

  const setAndFireProducts = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setFiredCardIndexes(new Set([...firedCardIndexes, ...visibleCardIndexes]));
      loadSendEventsToMonetate({
        recToken: recTokens?.filter((_, i) => visibleCardIndexes.has(i)),
      });
      visibleCardIndexes.clear();
    }, 1000);
  };

  return (
    <div className={`recommendation-main-container-new ${className}`}>
      <section className="recommendations-tile-new">
        {products?.length > 0 && (
          <Row className="recommendations-wrapper">
            <div>
              {shouldShowHeader &&
                !isBIACombinedCarouselEnabled &&
                !fromBIA &&
                renderHeader({ headerLabel: headerLabelCapitalized, params, fromOrderDetails })}
              {fromOrderDetails && renderSubHeader({ subHeaderLabel })}
            </div>
            {accordionHeader &&
              renderAccordionHeader({
                headerLabel: headerLabelCapitalized,
                handleAccordionToggle,
                accordionOpenClass,
                isPdpPlaProducts,
              })}
            <div ref={ref} className="recommendation-product-section">
              {renderRecommendationList({
                products,
                variations,
                isHideOriginalPriceAndMarchantTagEnabled,
                isDynamicBadgeEnabled,
                headerLabel: headerLabelCapitalized,
                page,
                currencySymbol,
                currencyAttributes,
                onQuickViewOpenClick,
                showSkeletonWithLazyLoad,
                isAddedToBag,
                bagItemCount,
                isBagPage,
                isBackToQuickviewCTAEnabled,
                isQvModalOpen,
                closeQvModal,
                serverTime,
                promotionsData,
                isPromoMsgEnabledRecommendation,
                visibleCardIndexes,
                setVisibleCardIndexes,
                loadSendEventsToMonetate,
                recTokens,
                firedCardIndexes,
                setAndFireProducts,
                isMobile,
                isDynamicBadgeEnabledAbtest,
                isTransparentImageSizeReduceEnabled,
              })}
            </div>
          </Row>
        )}
      </section>
    </div>
  );
};

export { RecommendationCarousel as RecommendationCarouselVanilla };

export default errorBoundary(withStyles(RecommendationCarousel, style));
